<template>
  <component
    :is="resolvedTag"
    v-bind="linkAttrs"
    :style="{ '--btn-color': color }"
    :class="[
      'btn-v2',
      'tw:relative tw:inline-flex tw:items-center tw:justify-center tw:gap-2',
      'tw:font-semibold tw:leading-none tw:whitespace-nowrap tw:rounded-[8px]',
      'tw:transition-all tw:duration-150 tw:cursor-pointer tw:border tw:outline-none',
      sizeClass,
      variantClass,
      { 'tw:opacity-50 tw:cursor-not-allowed tw:pointer-events-none': disabled || loading },
      { 'tw:pointer-events-none': href && disabled },
      { 'skeleton-play': skeleton },
    ]"
    data-testid="button-play-v2"
    @click="handleClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <span
      class="tw:flex tw:items-center tw:gap-2 tw:leading-none"
      :class="{ 'tw:invisible': loading }"
      data-testid="button-play-v2-content"
    >
      <IconGoogle v-if="icon" :name="icon" :size="iconSize" :color="activeTextColor" />
      <TextPlay
        v-if="text"
        tag="span"
        :text="text"
        :size="textSize"
        weight="semibold"
        :color="activeTextColor"
        data-testid="button-play-v2-text"
      />
      <IconGoogle v-if="iconRight" :name="iconRight" :size="iconSize" :color="activeTextColor" />
    </span>

    <span
      v-if="loading"
      class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:animate-spin"
      data-testid="button-play-v2-spinner"
      aria-hidden="true"
    >
      <IconGoogle name="progress_activity" :size="iconSize" :color="activeTextColor" />
    </span>
  </component>
</template>

<script lang="ts" setup>
import chroma from 'chroma-js'
import { computed, ref, onMounted, onUnmounted, watch, resolveComponent } from 'vue'
import { type RouteLocationRaw } from 'vue-router'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

const props = withDefaults(defineProps<{
  text?: string
  icon?: string
  iconRight?: string
  variant?: 'filled' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  color?: string
  disabled?: boolean
  loading?: boolean
  skeleton?: boolean
  type?: 'button' | 'submit' | 'reset'
  href?: string
  to?: string | object | RouteLocationRaw
}>(), {
  variant: 'filled',
  size: 'md',
  color: 'var(--maincolor)',
  text: undefined,
  icon: undefined,
  iconRight: undefined,
  disabled: false,
  loading: false,
  skeleton: false,
  type: 'button',
  href: undefined,
  to: undefined,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.loading && !props.skeleton) {
    emit('click', event)
  }
}

const resolvedTag = computed(() => {
  if (props.href) return 'a'
  if (props.to) return resolveComponent('RouterLink')
  return 'button'
})

const linkAttrs = computed(() => {
  if (props.href) return { href: props.href, target: '_blank', rel: 'noopener noreferrer' }
  if (props.to) return { to: props.to }
  return { type: props.type, disabled: props.disabled || props.loading }
})

// White or black — whichever contrasts better against the button's brand color.
// Used by filled variant (always) and by outline on hover (when bg becomes brand color).
const filledTextColor = ref<'#ffffff' | '#000000'>('#ffffff')

const isHovered = ref(false)

function resolveButtonColor(): string | null {
  const raw = props.color.trim()

  // Direct color value (hex, rgb, hsl, named…)
  try { chroma(raw); return raw } catch { /* not a direct color */ }

  // CSS variable reference — e.g. var(--maincolor)
  const varName = raw.match(/var\(\s*(--[\w-]+)/)?.[1]
  if (!varName) return null

  // data-color is set by defineMainColor() alongside --maincolor and is
  // a plain attribute — always readable, no CSS cascade timing issues.
  if (varName === '--maincolor') {
    const fromAttr = document.documentElement.getAttribute('data-color')
    if (fromAttr) return fromAttr
  }

  // General fallback: read the inline style property directly
  return (
    document.documentElement.style.getPropertyValue(varName).trim() ||
    document.body.style.getPropertyValue(varName).trim() ||
    null
  )
}

function updateTextColor() {
  const resolved = resolveButtonColor()
  if (!resolved) return
  try {
    const bg = chroma(resolved)
    const [l] = bg.oklch()
    filledTextColor.value = l < 0.5 ? '#ffffff' : '#000000'
  } catch { /* keep current value */ }
}

let observer: MutationObserver | null = null

onMounted(() => {
  updateTextColor()

  observer = new MutationObserver(updateTextColor)
  // defineMainColor() sets both 'data-color' attr and '--maincolor' inline style
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style', 'class', 'data-theme', 'data-color'],
  })
  observer.observe(document.body, { attributes: true, attributeFilter: ['style'] })
})

onUnmounted(() => { observer?.disconnect(); observer = null })

watch(() => props.color, updateTextColor)

const sizeClass = computed(() => ({
  sm: 'tw:h-8 tw:px-3 tw:text-xs',
  md: 'tw:h-[46px] tw:px-4 tw:text-sm',
  lg: 'tw:h-[52px] tw:px-5 tw:text-base',
}[props.size]))

const variantClass = computed(() => ({
  filled: 'btn-v2--filled',
  outline: 'btn-v2--outline',
  ghost: 'btn-v2--ghost',
}[props.variant]))

const iconSize = computed(() => ({ sm: '14px', md: '16px', lg: '18px' }[props.size]))

const textSize = computed(() => ({ sm: 'xs', md: 'sm', lg: 'base' } as const)[props.size])

// filled: always computed contrast color (white or black)
// outline hover: background becomes brand color → use computed contrast color
// outline normal / ghost: text = brand color (same as border for outline, seller brand for ghost)
const activeTextColor = computed(() => {
  if (props.variant === 'filled') return filledTextColor.value
  if (props.variant === 'outline' && isHovered.value) return filledTextColor.value
  return 'var(--btn-color)'
})
</script>

<style scoped>
.btn-v2--filled {
  background-color: var(--btn-color);
  border-color: var(--btn-color);
}
.btn-v2--filled:hover:not(:disabled) {
  opacity: 0.88;
}
.btn-v2--filled:focus-visible {
  outline: 2px solid var(--btn-color);
  outline-offset: 2px;
}

.btn-v2--outline {
  background-color: transparent;
  border-color: var(--btn-color);
  color: var(--btn-color);
}
.btn-v2--outline:hover:not(:disabled) {
  background-color: var(--btn-color);
}
.btn-v2--outline:focus-visible {
  outline: 2px solid var(--btn-color);
  outline-offset: 2px;
}

.btn-v2--ghost {
  background-color: transparent;
  border-color: transparent;
  color: var(--btn-color);
}
.btn-v2--ghost:hover:not(:disabled) {
  background-color: color-mix(in oklab, var(--btn-color) 10%, transparent);
}
.btn-v2--ghost:focus-visible {
  outline: 2px solid var(--btn-color);
  outline-offset: 2px;
}

.btn-v2.skeleton-play > * {
  opacity: 0;
}

.btn-v2.skeleton-play {
  border-color: transparent !important;
  background-color: transparent !important;
}
</style>
